import styled, { css } from 'styled-components';

import { mediaUp, rem } from 'approot/shared/style';
import { fontWeightBold } from 'approot/shared/styles/typography.style';
import {
  spacing16,
  spacing8,
  spacing12,
  spacing4,
} from 'approot/shared/styles/spacing.style';
import {
  azure1,
  green1,
  green7,
  grey1,
  grey3,
  grey5,
  grey6,
  grey9,
  purple1,
  purple7,
  red1,
  red7,
  red9,
} from 'approot/shared/styles/colours.style';
import { flexCentreAlignCss } from 'approot/shared/styles/flex.style';
import { COMPONENT_LAYOUT_GRID } from 'global/constants';
import { useActivityBuilderFontStyles } from 'approot/activity-builder/theme/activity-builder-theme.style';
import {
  baseBoxShadowFocus,
  borderRadius8,
} from 'approot/shared/styles/borders.style';
import { MultipleChoiceRadioInputLabel } from './list/multiple-choice-radio.style';
import { MultipleChoiceCheckboxLabel } from './list/multiple-choice-check.style';

export type MultipleChoiceImageProps = {
  $imageUrl?: string | null;
};

export type OptionSizeProps = {
  $minHeight: number;
};
export const optionSize = css<OptionSizeProps>`
  ${({ $minHeight }) =>
    $minHeight &&
    `
    min-height: ${rem($minHeight)};
  `}
`;

export type ItemsPerRowProps = {
  $itemsPerRow: number;
};
export const itemsPerRowCss = css<ItemsPerRowProps>`
  flex-basis: calc(
    (100% - (${({ $itemsPerRow }) => $itemsPerRow} - 1) * 8px) /
      ${({ $itemsPerRow }) => $itemsPerRow}
  );
`;

export const MultipleChoiceLetter = styled.span`
  ${flexCentreAlignCss}
  ${() =>
    useActivityBuilderFontStyles({
      isSmall: true,
    })}

  flex-shrink: 0;
  font-weight: ${fontWeightBold};
  margin-right: ${rem(spacing12)};
  position: relative;
`;

export const MultipleChoiceImageLetter = styled.span`
  ${flexCentreAlignCss}

  background-color: ${grey1};
  border: 1px solid ${grey5};
  border-radius: ${rem(40)};
  flex-shrink: 0;
  width: ${rem(40)};
  height: ${rem(40)};
  font-size: ${rem(24)};
  font-weight: ${fontWeightBold};
  margin-right: ${rem(spacing16)};
  position: absolute;
  top: ${rem(spacing16)};
  left: ${rem(spacing16)};
  z-index: 1;
`;

export const MultiChoiceCorrectIcon = styled.div`
  top: -${rem(spacing8)};
  right: -${rem(spacing8)};
  position: absolute;
  z-index: 1;
  svg {
    stroke: none;
  }
`;

export const MultiChoiceListCorrectIcon = styled.div`
  margin-right: ${rem(spacing8)};
  svg {
    stroke: none;
  }
`;

type MultipleChoiceButtonsProps = {
  $display?: string;
  $isResponsive?: boolean;
};
export const MultipleChoiceItems = styled.div<MultipleChoiceButtonsProps>`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing8)};
  margin: auto;
  width: 100%;

  label,
  ${MultipleChoiceRadioInputLabel}, ${MultipleChoiceCheckboxLabel} {
    min-height: ${rem(40)};
  }

  ${({ $display, $isResponsive }) =>
    $display === COMPONENT_LAYOUT_GRID &&
    css`
      ${$isResponsive
        ? mediaUp.md`
        flex-direction: row;
        flex-wrap: wrap;
      `
        : `
        flex-direction: row;
        flex-wrap: wrap;
      `}
    `}
`;

export type MultipleChoiceItemStatusProps = {
  $isActive?: boolean;
  $isDisabled?: boolean;
  $isSelected?: boolean;
  $isCorrect?: boolean;
  $isIncorrect?: boolean;
};

export const multipleChoiceItemStatusCss = css<MultipleChoiceItemStatusProps>`
  ${({ $isActive }) =>
    $isActive &&
    `
      background-color: ${azure1};
      box-shadow: ${baseBoxShadowFocus};
      &:focus {
        border-color: ${grey9};
      }
    `}

  ${({ $isCorrect }) =>
    $isCorrect &&
    `
      background-color: ${green1};
      border-color: ${green7};
      box-shadow: 0 0 0 1px ${green7};

      &:focus {
        box-shadow: 0 0 0 2px ${green7};
        border-color: ${green7};
      }
    `}

  ${({ $isIncorrect }) =>
    $isIncorrect === true &&
    `
      background-color: ${red1};
      border-color: ${red7};
      box-shadow: 0 0 0 1px ${red7};

      &:focus {
        box-shadow: 0 0 0 2px ${red7};
        border-color: ${red9};
      }
    `}

  ${({ $isSelected }) =>
    $isSelected &&
    `
      background-color: ${purple1};
      border-color: ${purple7};
      box-shadow: 0 0 0 1px ${purple7};

      &:focus {
        box-shadow: 0 0 0 2px ${purple7};
        border-color: ${purple7};
      }
    `}

  ${({ $isDisabled }) =>
    $isDisabled === true &&
    `
      background-color: ${grey3};
      border-color: ${grey5};
      box-shadow: none;
      color: ${grey6};

      &:active {
        box-shadow: none;
      }

      &:focus {
        box-shadow: 0 0 0 2px ${grey9};
        border-color: ${grey6};
      }

      ${MultipleChoiceLetter} {
        background-color: ${grey3};
      }

      &:before {
        content: '';
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: ${rem(borderRadius8)};
        position: absolute;
        top: ${rem(spacing4)};
        bottom: ${rem(spacing4)};
        left: ${rem(spacing4)};
        right: ${rem(spacing4)};
        z-index: 1;
      }
    `}
`;
